import React, { ComponentProps, ComponentType, ReactElement } from "react";
import * as _ from "lodash";
import { Space, Select } from "antd";

import { classname } from "@ctra/utils";

import styles from "./Dropdown.module.less";
import { Icon } from "../../../../elements";

export type DropdownProps = Omit<ComponentProps<typeof Select>, "onSelect" | "onChange"> & {
  /**
   * placeholder
   */
  placeholder?: string | React.ReactNode;
  /**
   * possible values
   */
  values: Array<{ value: string; label: string }>;
  /**
   * selected values
   */
  selection: string;
  /**
   * Handler to call when the user clicks on the list items
   */
  onChange: (selection: string) => void;
  /**
   * open state (mainly for unit tests)
   */
  open?: boolean;
  /**
   * filter options by input
   */
  filterOption?: ComponentProps<typeof Select>["filterOption"];
  /**
   * disabled
   */
  disabled?: ComponentProps<typeof Select>["disabled"];
  /**
   * Icon
   */
  icon?: ReactElement;
};

/**
 * Dropdown with simple API
 * @param {Array<{value: string, label: string}>} values
 * @param {string} selection
 * @param {string | React.ReactElement<any, string | React.JSXElementConstructor<any>> | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} placeholder
 * @param {(selection: string) => void} onChange
 * @param {boolean | undefined} open
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | undefined} icon
 * @param {any} className
 * @param {Omit<React.PropsWithChildren<DropdownProps>, "selection" | "onChange" | "values" | "icon" | "className" | "placeholder" | "open">} rest
 * @returns {React.ReactElement}
 * @constructor
 */
function Dropdown({
  values,
  selection,
  placeholder,
  onChange,
  open,
  icon,
  className,
  ...rest
}: React.PropsWithChildren<DropdownProps>): React.ReactElement {
  /**
   * Handle change of selection
   */
  const handleChange = (value: string) => {
    onChange(value);
  };

  return (
    <Select
      suffixIcon={icon}
      open={open}
      className={classname("ctra-charts-Dropdown", styles.Select, className)}
      // @ts-ignore
      value={selection}
      style={{ minWidth: 200 }}
      placeholder={placeholder}
      showArrow
      onChange={handleChange}
      {...rest}
    >
      {_.map(values, ({ label, value }) => (
        <Select.Option key={value} value={value}>
          {label}
        </Select.Option>
      ))}
    </Select>
  );
}

export default Dropdown;
